import classNames from "classnames";
import React from "react";
import autoBind from "react-autobind";
import TaskActions from "~/actions/task_actions";
import DateTimePicker from "~/components/forms/DateTimePicker";
import AsyncSelect from "~/components/forms/dropdowns/SelectWrapper/AsyncSelect";
import Select from "~/components/forms/dropdowns/SelectWrapper/Select";
import { TeammatePicker } from "~/components/forms/dropdowns/TeamMemberSelect";
import TaskEditor from "~/components/forms/HtmlEditors/TaskEditor";
import TaskTemplateSelect from "~/components/forms/Templates/TaskTemplateSelect";
import VariableMenuButton from "~/components/forms/VariableMenuButton";
import InsertDocumentButton from "~/components/insert_document_button";
import UnsupportedVariablesWarning from "~/components/modals/UnsupportedVariablesWarning";
import { TASK_CATEGORY_OPTIONS } from "~/constants/dropdowns/TaskCategoryOptions";
import { TASK_PRIORITY_OPTIONS } from "~/constants/dropdowns/TaskPriorityOptions";
import { TASK_STATUS_OPTIONS } from "~/constants/dropdowns/TaskStatusOptions";
import { isAdminOrStaff } from "~/helpers/userRoleHelper";
import dayjs from "~/lib/dayjs";
import { normalizeUnsupportedVariablesError } from "~/lib/unsupportedVariables";
import TaskStore from "~/stores/task_store";

const taskStoreStateHandlers = {
  createTaskDone: (component) =>
    component.setState({ sending: false, errors: {} }),
  createTaskFail: (component, { errors }) =>
    component.setState({ sending: false, errors: errors || {} }),
};

const formatError = (error) =>
  Array.isArray(error) ? error.join(", ") : error;

class AddTaskForm extends React.Component {
  constructor(props) {
    super(props);

    const { lead } = this.props;

    this.state = {
      task: {
        name: "Follow Up",
        category: "follow_up",
        due_date_at: dayjs().format("YYYY-MM-DD"),
        description: "",
        owner_id: Rails.helpers.currentUser.id,
        priority: "normal",
        status: "to_do",
        lead_ids: [lead.id],
      },
      errors: {},
      sending: false,
      templateDescription: null,
      linksToInsert: [],
      variablesWarning: null,
    };

    autoBind(this);
    this.taskNameRef = React.createRef();
  }

  componentDidMount() {
    this.taskStoreListener = TaskStore.addListener(this.onTaskStoreChange);
  }

  componentWillUnmount() {
    this.taskStoreListener?.remove();
  }

  onTaskStoreChange() {
    const taskStoreState = TaskStore.getState();
    taskStoreStateHandlers[taskStoreState.lastTaskStoreAction]?.(
      this,
      taskStoreState,
    );
  }

  setTaskField(name, val) {
    this.setState((prevState) => ({
      task: { ...prevState.task, [name]: val },
    }));
  }

  onFormSubmit = (e, saveAnyway = false) => {
    e.preventDefault();

    const { task } = this.state;
    const { lead } = this.props;

    this.setState({ sending: true });

    const errors = this.validate();

    if (_lodash.size(errors) === 0) {
      TaskActions.createTask(task, lead.id, {
        saveAnyway,
        onUnsupportedVariables: (warning) =>
          this.setState({
            sending: false,
            variablesWarning: normalizeUnsupportedVariablesError(warning),
          }),
        onDone: () =>
          this.setState({
            sending: false,
            variablesWarning: null,
          }),
        onFail: () => {
          this.setState({ sending: false, variablesWarning: null });
          GlobalContainer.notify(
            "Failed to save task. Please try again.",
            "error",
          );
        },
      });
    } else {
      this.setState({ sending: false, errors });
    }
  };

  validate() {
    const { task } = this.state;
    const errors = {};

    if (!task.name) {
      errors.name = "Can't be empty";
    }

    if (!task.category) {
      errors.category = "Can't be empty";
    }

    if (!task.due_date_at) {
      errors.due_date_at = "Can't be empty";
    }

    if (!task.priority) {
      errors.priority = "Can't be empty";
    }

    if (!task.status) {
      errors.status = "Can't be empty";
    }

    if (_lodash.isEmpty(task.lead_ids)) {
      errors.lead_ids = "Can't be empty";
    }

    this.setState({ errors });

    return errors;
  }

  applyTemplate(template) {
    if (template) {
      const taskUpdates = {
        name: template.name || "Follow Up",
        category: template.category || "follow_up",
        description: template.description || "",
        priority: template.priority || "normal",
      };
      if (template.owner_id) {
        taskUpdates.owner_id = template.owner_id;
      }
      if (template.status) {
        taskUpdates.status = template.status;
      }
      this.setState((prevState) => ({
        task: { ...prevState.task, ...taskUpdates },
        templateDescription: template.description || "",
        errors: {},
      }));
    } else {
      this.setState((prevState) => ({
        task: {
          ...prevState.task,
          name: "Follow Up",
          category: "follow_up",
          description: "",
          priority: "normal",
        },
        templateDescription: "",
        errors: {},
      }));
    }
  }

  render() {
    const { task, sending, errors, linksToInsert, variablesWarning } =
      this.state;
    const descriptionError = errors.description || errors.base;
    const submitButtonClass = classNames("btn btn-primary", {
      disabled: sending,
    });
    const variablesEnabled = isAdminOrStaff();

    return (
      <>
        <form onSubmit={this.onFormSubmit}>
          <div className="form-row">
            <div className="form-group col-md-4">
              <label htmlFor="task_name">Task Name</label>
              <div className="input-group">
                <input
                  ref={this.taskNameRef}
                  type="text"
                  id="task_name"
                  name="name"
                  className={classNames(
                    "form-control",
                    "d-block",
                    errors.name ? "has-error" : "",
                  )}
                  placeholder="Enter Name"
                  value={task.name}
                  onChange={(val) =>
                    this.setTaskField("name", val && val.target.value)
                  }
                />
                {variablesEnabled && (
                  <div className="input-group-append">
                    <VariableMenuButton
                      inputRef={this.taskNameRef}
                      value={task.name}
                      onChange={(value) => this.setTaskField("name", value)}
                      includeAlertVariables
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="form-group col-md-4">
              <label htmlFor="task-category">Task Category</label>
              <Select
                id="task-category"
                name="category"
                placeholder="Select Category"
                options={TASK_CATEGORY_OPTIONS}
                value={task.category}
                isInvalid={!!errors.category}
                onChange={(val) =>
                  this.setTaskField("category", val?.value || "")
                }
                isSearchable
              />
            </div>
            <div className="form-group col-md-4">
              <label htmlFor="task_due_date_at">Due Date</label>
              <DateTimePicker
                id="task_due_date_at"
                placeholder="Select Due Date"
                format="LL"
                pickerType="date"
                minDate={dayjs().startOf("day").toDate()}
                value={dayjs(task.due_date_at)?.format("LL")}
                onChange={(picker) =>
                  this.setTaskField("due_date_at", picker && picker.toDate())
                }
              />
            </div>
          </div>

          <div className="form-group mb15">
            <label htmlFor="task-description" className="d-block">
              Description
            </label>
            <TaskEditor
              className={descriptionError ? "tiny-mce has-error" : ""}
              onChange={(html) => this.setTaskField("description", html)}
              placeholder="Enter Description"
              leadId={this.props.lead.id}
              enableVariables={variablesEnabled}
              includeOwnerVariables
              includeAlertVariables
              linksToInsert={linksToInsert}
              clearLinksToInsert={() => {
                this.setState({ linksToInsert: [] });
              }}
              replaceValue={this.state.templateDescription}
              clearReplaceValue={() =>
                this.setState({ templateDescription: null })
              }
            />
            {descriptionError && (
              <div className="invalid-feedback d-block">
                {formatError(descriptionError)}
              </div>
            )}
          </div>

          <div className="form-row">
            <div className="form-group col-lg-8">
              <label htmlFor="leads-tasks-select" className="d-block">
                Related Contacts
              </label>
              <AsyncSelect
                id="leads-tasks-select"
                placeholder="Please Select Contacts ..."
                resourcePath="leads"
                formatOption={(lead) => ({
                  value: lead.id,
                  label: lead.name,
                })}
                isMulti
                isSearchable
                isInvalid={!!errors.lead_ids}
                value={task.lead_ids}
                onChange={(values) =>
                  this.setTaskField(
                    "lead_ids",
                    values ? values.map((item) => item.value) : [],
                  )
                }
              />
            </div>
            <div className="form-group col-lg-4">
              <TaskTemplateSelect
                applyTemplate={this.applyTemplate}
                name={task.name}
                category={task.category}
                description={task.description}
                priority={task.priority}
                owner_id={task.owner_id}
                status={task.status}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group col-md-4">
              <label htmlFor="task_owner_id" className="form-control-label">
                Owner
              </label>
              <TeammatePicker
                id="task_owner_id"
                name="owner_id"
                placeholder="Owner"
                value={task.owner_id}
                onChange={(opt) =>
                  this.setTaskField("owner_id", opt && opt.value)
                }
                isClearable
              />
            </div>

            <div className="form-group col-md-4">
              <label htmlFor="task-priority">Priority</label>
              <Select
                id="task-priority"
                name="priority"
                placeholder="Select Priority"
                value={task.priority}
                options={TASK_PRIORITY_OPTIONS}
                isSearchable
                isInvalid={!!errors.priority}
                onChange={(val) =>
                  this.setTaskField("priority", val?.value || "")
                }
              />
            </div>

            <div className="form-group col-md-4">
              <label htmlFor="task-status">Status</label>
              <Select
                id="task-status"
                name="status"
                placeholder="Select Status"
                value={task.status}
                options={TASK_STATUS_OPTIONS}
                isSearchable
                isInvalid={!!errors.status}
                onChange={(val) =>
                  this.setTaskField("status", val?.value || "")
                }
              />
            </div>
          </div>

          <div className="form-group pt10 d-flex align-items-center">
            <InsertDocumentButton
              setLinksToInsert={(links) =>
                this.setState({ linksToInsert: links })
              }
            />
            <div className="ml-auto">
              {sending ? (
                <button
                  type="button"
                  className={submitButtonClass}
                  disabled={sending}
                >
                  Creating...
                </button>
              ) : (
                <button type="submit" className={submitButtonClass}>
                  Add Task
                </button>
              )}
            </div>
          </div>
        </form>
        <UnsupportedVariablesWarning
          show={!!variablesWarning}
          warning={variablesWarning}
          saving={sending}
          onReview={() => this.setState({ variablesWarning: null })}
          onSaveAnyway={() =>
            this.setState({ variablesWarning: null }, () =>
              this.onFormSubmit({ preventDefault: () => false }, true),
            )
          }
        />
      </>
    );
  }
}

export default AddTaskForm;
